import { useEffect, useState } from "react";
import { Bell, EnvelopeSimple, WhatsappLogo, BellRinging, FloppyDisk, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";

const KINDS = [
  { key: "session_reminders", label: "Session reminders", desc: "24h before each booking" },
  { key: "progress_comment", label: "Coach feedback", desc: "When your coach leaves a note on your progress" },
  { key: "billing_upcoming", label: "Upcoming renewal", desc: "Heads-up ~3 days before your monthly auto-payment" },
  { key: "billing_failed", label: "Payment failed", desc: "Critical: your monthly auto-payment didn't go through" },
  { key: "invoice", label: "Invoices", desc: "When a new invoice is issued or due" },
  { key: "pot_low", label: "Low balance", desc: "Only fires if you're not on a monthly plan and your prepaid balance is running low" },
  { key: "monthly_statement", label: "Monthly statement", desc: "Auto-billing receipt at the start of each month" },
  { key: "metric_checkin_nudge", label: "Weekly check-in nudge", desc: "Gentle reminder if you haven't logged weight / body-fat in a while" },
];
const CHANNELS = [
  { key: "email", label: "Email", icon: EnvelopeSimple },
  { key: "whatsapp", label: "WhatsApp", icon: WhatsappLogo, gateKey: "has_whatsapp_number", gateLabel: "Needs a WhatsApp number on your profile" },
  { key: "push", label: "Push", icon: BellRinging, gateKey: "has_push_subscription", gateLabel: "Enable push notifications first" },
];

export default function ClientSettings() {
  const [prefs, setPrefs] = useState(null);
  const [meta, setMeta] = useState({ has_whatsapp_number: false, has_push_subscription: false });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/me/notification-prefs");
      setPrefs(data.prefs);
      setMeta({
        has_whatsapp_number: !!data.has_whatsapp_number,
        has_push_subscription: !!data.has_push_subscription,
      });
      setDirty(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load preferences");
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = (kind, channel) => {
    setPrefs((p) => ({
      ...p,
      [kind]: { ...p[kind], [channel]: !p[kind][channel] },
    }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/me/notification-prefs", prefs);
      setPrefs(data.prefs);
      setDirty(false);
      toast.success("Notification preferences saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!prefs) {
    return <div className="tracker text-zinc-500">Loading…</div>;
  }

  return (
    <div className="space-y-8" data-testid="client-settings-page">
      <div>
        <div className="tracker text-brand mb-3 flex items-center gap-2"><Bell size={14} weight="duotone" /> [ Settings ]</div>
        <h1 className="font-display text-4xl sm:text-5xl">Notification preferences.</h1>
        <p className="text-zinc-500 text-xs mt-2">Choose how and when we reach you. Defaults keep email on for everything.</p>
      </div>

      <div className="card-flat overflow-x-auto" data-testid="notification-prefs-table">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left tracker px-6 py-3 text-zinc-400">Notification</th>
              {CHANNELS.map((ch) => (
                <th key={ch.key} className="text-center tracker px-3 py-3 text-zinc-400">
                  <span className="inline-flex items-center gap-1.5">
                    <ch.icon size={14} weight="duotone" /> {ch.label}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {KINDS.map((k) => (
              <tr key={k.key} className="border-b border-white/5" data-testid={`prefs-row-${k.key}`}>
                <td className="px-6 py-4">
                  <div className="font-semibold text-sm">{k.label}</div>
                  <div className="text-zinc-500 text-xs mt-0.5">{k.desc}</div>
                </td>
                {CHANNELS.map((ch) => {
                  const gated = ch.gateKey && !meta[ch.gateKey];
                  const on = prefs[k.key]?.[ch.key];
                  return (
                    <td key={ch.key} className="text-center px-3 py-4">
                      <label className={`inline-flex items-center cursor-pointer ${gated ? "opacity-40 cursor-not-allowed" : ""}`} title={gated ? ch.gateLabel : undefined}>
                        <input
                          type="checkbox"
                          checked={!!on}
                          disabled={gated}
                          onChange={() => !gated && toggle(k.key, ch.key)}
                          className="sr-only peer"
                          data-testid={`prefs-toggle-${k.key}-${ch.key}`}
                        />
                        <div className={`w-10 h-5 rounded-full transition-colors ${on ? "bg-emerald-500" : "bg-zinc-700"} relative`}>
                          <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${on ? "translate-x-5" : ""}`} />
                        </div>
                      </label>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          className="btn-primary !py-2.5 !px-5 !text-xs disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="prefs-save"
        >
          <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </button>
        {(!meta.has_whatsapp_number || !meta.has_push_subscription) && (
          <div className="flex items-center gap-2 text-zinc-500 text-xs">
            <Warning size={12} weight="duotone" /> Grey toggles are unavailable until you add a WhatsApp number / enable push.
          </div>
        )}
      </div>
    </div>
  );
}
